import type { Cell } from '../types';
import { checkForYugos, processYugos } from './yugos';

// Board validation functions

export const isValidMove = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  if (row < 0 || row >= 8 || col < 0 || col >= 8) return false;
  if (board[row][col] !== null) return false;
  return !wouldCreateLineTooLong(board, row, col, playerColor);
};

export const wouldCreateLineTooLong = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  const directions = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];

  for (const [dr, dc] of directions) {
    let count = 1;
    
    // Count in positive direction
    let r = row + dr, c = col + dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && 
           board[r][c] && board[r][c]!.color === playerColor) {
      count++;
      r += dr;
      c += dc;
    }
    
    // Count in negative direction
    r = row - dr;
    c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && 
           board[r][c] && board[r][c]!.color === playerColor) {
      count++;
      r -= dr;
      c -= dc;
    }
    
    if (count > 4) return true;
  }
  
  return false;
};

export const hasLegalMoves = (board: (Cell | null)[][], playerColor: 'white' | 'black'): boolean => {
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (isValidMove(board, row, col, playerColor)) {
        return true;
      }
    }
  }
  return false;
};

export const getAllValidMoves = (board: (Cell | null)[][], playerColor: 'white' | 'black'): {row: number, col: number}[] => {
  const moves = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (isValidMove(board, row, col, playerColor)) {
        moves.push({row, col});
      }
    }
  }
  return moves;
};

export const copyBoard = (board: (Cell | null)[][]) => board.map(r => [...r]);

export const isEmptyCell = (board: (Cell | null)[][], row: number, col: number): boolean => {
  return !board[row][col] || !board[row][col]?.isYugo;
};

export const makeMove = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): (Cell | null)[][] => {
  const newBoard = board.map(r => [...r]);
  newBoard[row][col] = { color: playerColor, isYugo: false };
  
  // Process yugos if any
  const yugos = checkForYugos(newBoard, row, col, playerColor);
  if (yugos.length > 0) {
    const result = processYugos(newBoard, yugos, row, col);
    if (result.yugoFormed) {
      newBoard[row][col] = { color: playerColor, isYugo: true };
    }
  }
  
  return newBoard;
};

